// FILE: src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { Edit, PlusCircle, Trash2, Save } from 'lucide-react';

export default function Dashboard({ user, myRestaurant, setMyRestaurant }) {
    const [bookings, setBookings] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(myRestaurant);
    const [voucherData, setVoucherData] = useState({ description: '', details: '' });

    const appId = 'goeatout-app';

    useEffect(() => {
        if (!user || !myRestaurant) return;
        const bookingsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/bookings`),
            where("ownerId", "==", user.uid)
        );
        const unsubscribe = onSnapshot(bookingsQuery, (snapshot) => {
            const bookingsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setBookings(bookingsData);
        });
        return () => unsubscribe();
    }, [user, myRestaurant]);

    const handleUpdateRestaurant = async (e) => {
        e.preventDefault();
        if (!myRestaurant) return;
        const restaurantDocRef = doc(db, `/artifacts/${appId}/public/data/restaurants`, myRestaurant.id);
        try {
            await updateDoc(restaurantDocRef, { name: editData.name, cuisine: editData.cuisine, address: editData.address });
            setMyRestaurant(prev => ({ ...prev, ...editData }));
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating restaurant:", error);
        }
    };

    const handleVoucherUpdate = async (vouchers) => {
        const restaurantDocRef = doc(db, `/artifacts/${appId}/public/data/restaurants`, myRestaurant.id);
        try {
            await updateDoc(restaurantDocRef, { vouchers });
            setMyRestaurant(prev => ({ ...prev, vouchers }));
        } catch (error) {
            console.error("Error updating vouchers:", error);
        }
    };

    const handleAddVoucher = (e) => {
        e.preventDefault();
        const newVouchers = [...(myRestaurant.vouchers || []), { id: `v${Date.now()}`, ...voucherData }];
        handleVoucherUpdate(newVouchers);
        setVoucherData({ description: '', details: '' });
    };
    
    const handleDeleteVoucher = (index) => {
        const newVouchers = myRestaurant.vouchers.filter((_, i) => i !== index);
        handleVoucherUpdate(newVouchers);
    };


    if (!myRestaurant) {
        return (
            <div className="container mx-auto p-8 text-center">
                <h2 className="text-2xl font-bold">Loading Your Dashboard...</h2>
                <p>You are logged in, but not assigned as an owner to any restaurant.</p>
            </div>
        );
    }
    
    return (
        <div className="container mx-auto p-4 sm:p-8 animate-fade-in-up">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-6">Restaurant Dashboard</h1>
            
            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">My Restaurant: {myRestaurant.name}</h2>
                    <button onClick={() => { setIsEditing(!isEditing); setEditData(myRestaurant); }} className="flex items-center text-blue-600 font-semibold">
                        <Edit className="w-4 h-4 mr-2" /> {isEditing ? 'Cancel' : 'Edit Details'}
                    </button>
                </div>
                {isEditing ? (
                    <form onSubmit={handleUpdateRestaurant} className="mt-4 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Restaurant Name</label>
                            <input type="text" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} className="w-full p-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Cuisine</label>
                            <input type="text" value={editData.cuisine} onChange={e => setEditData({...editData, cuisine: e.target.value})} className="w-full p-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Address</label>
                            <input type="text" value={editData.address} onChange={e => setEditData({...editData, address: e.target.value})} className="w-full p-2 border rounded-md"/>
                        </div>
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"><Save size={18} /> Save Changes</button>
                    </form>
                ) : (
                    <div className="mt-4 text-gray-600">
                        <p><span className="font-semibold">Cuisine:</span> {myRestaurant.cuisine}</p>
                        <p><span className="font-semibold">Address:</span> {myRestaurant.address}</p>
                    </div>
                )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Manage Vouchers</h2>
                <div className="space-y-3">
                    {(myRestaurant.vouchers || []).map((voucher, index) => (
                        <div key={voucher.id} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <p className="font-semibold">{voucher.description}</p>
                                <p className="text-sm text-gray-500">{voucher.details}</p>
                            </div>
                            <button onClick={() => handleDeleteVoucher(index)} className="text-red-500 hover:text-red-700"><Trash2 size={18}/></button>
                        </div>
                    ))}
                </div>
                <form onSubmit={handleAddVoucher} className="mt-6 border-t pt-4 flex items-center gap-2">
                    <input type="text" placeholder="New voucher description" value={voucherData.description} onChange={e => setVoucherData({...voucherData, description: e.target.value})} className="p-2 border rounded-md w-1/3" required/>
                    <input type="text" placeholder="Details (e.g., Valid Mon-Fri)" value={voucherData.details} onChange={e => setVoucherData({...voucherData, details: e.target.value})} className="p-2 border rounded-md flex-grow"/>
                    <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"><PlusCircle size={18}/> Add Voucher</button>
                </form>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Recent Bookings</h2>
                <div className="space-y-4">
                    {bookings.length > 0 ? bookings.map(b => (
                        <div key={b.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <p className="font-bold">{b.guests} guests on {new Date(b.date).toLocaleDateString()}</p>
                            <p className="text-sm text-gray-600">Time: {b.time}, Voucher: "{b.voucher.description}"</p>
                            <p className="text-xs text-gray-400 mt-1">Booked on: {b.bookedAt.toDate().toLocaleString()}</p>
                        </div>
                    )) : <p>No new bookings yet.</p>}
                </div>
            </div>
        </div>
    );
}
